import { bezSierotek } from '@insolitum/ui';
import { X, Loader2, Building2, UserCheck, Clock, TriangleAlert } from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  IKONA_BLEDU,
  IKONA_OSTRZEZENIA,
  IKONA_SUKCESU,
  UkladWejscia,
} from '../../components/marka/UkladWejscia';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../providers/AuthProvider';

import { rozpoznajOdmoweZaproszenia } from './odmowaZaproszenia';

/**
 * Po nieudanym `/auth/v1/verify` GoTrue NIE loguje użytkownika — wraca na
 * `redirect_to` z parametrami błędu (`#error=access_denied&error_code=
 * otp_expired&...` w hashu; w torze PKCE te same pola idą w query).
 * Do 2026-08-27 ta strona ich nie czytała, a stan „brak sesji" traktowała
 * jako „sesja zaraz będzie" — więc wygasły link (linki mailowe GoTrue żyją
 * 60 minut, zaproszenie partnera kliknięte po ~4 h) kończył się spinnerem
 * kręcącym się bez końca, bez słowa o przyczynie.
 */
function bladZWeryfikacjiUrl(searchParams: URLSearchParams): {
  kod: string;
  opis: string;
} | null {
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const kod = hash.get('error_code') || searchParams.get('error_code');
  const error = hash.get('error') || searchParams.get('error');
  if (!kod && !error) return null;
  return {
    kod: kod || error || '',
    opis: hash.get('error_description') || searchParams.get('error_description') || '',
  };
}

/** Po ilu ms braku sesji przestajemy udawać, że „zaraz będzie". */
const LIMIT_OCZEKIWANIA_NA_SESJE_MS = 12_000;

/**
 * Karta zaproszenia stoi na desktopie w JEDNYM punkcie (`UkladWejscia
 * stalaGoraRem`), a nie na środku ekranu: wyśrodkowana szła za swoją
 * wysokością, więc przejście przetwarzanie → wynik przesuwało ją o 56 px
 * w górę (1440 × 900: karta na y 318,6 przy przetwarzaniu, 262,6 bez sesji,
 * 274,6 przy odmowie, 266,6 przy sukcesie). Liczba to połowa wysokości
 * najwyższego stanu — odmowa RPC (limit kont, inny adres): 398,8 px, połowa
 * 199,4 px ≈ 12,5rem — więc ten stan stoi tam, gdzie stałby wyśrodkowany,
 * a krótsze (przetwarzanie 262,8 px, brak sesji 374,8 px) rosną od tego
 * samego punktu tylko w dół. 1440 × 900: karta na y 250; 1280 × 800: karta na
 * y 200, najwyższy stan kończy się 201 px nad dołem. Do 16.10.2026 liczba
 * była 11,75rem (najwyższy stan 374,8 px): odmowa wypisywała wtedy podpowiedź
 * serwera, o wiersz krótszą od zdania katalogu. Stan, który urośnie, gasi
 * bramkę: `ekranyWejsciaWChromium.test.tsx` §11.
 */
const POLOWA_NAJWYZSZEGO_STANU_REM = 12.5;

export function AcceptInvitationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, refreshUserData } = useAuth();

  const [status, setStatus] = useState<
    'loading' | 'success' | 'error' | 'not_found' | 'link_expired' | 'no_session'
  >('loading');
  const [message, setMessage] = useState('');
  const [organizationName, setOrganizationName] = useState<string | null>(null);

  const organizationId = searchParams.get('org');
  const token = searchParams.get('token');

  // Błąd weryfikacji czytamy raz — hash znika przy nawigacjach wewnątrz SPA.
  const bladWeryfikacji = useMemo(() => bladZWeryfikacjiUrl(searchParams), [searchParams]);

  // Wygasły / zużyty link: bez sesji nie ma czego przetwarzać — od razu
  // uczciwy komunikat zamiast wiecznego spinnera.
  useEffect(() => {
    if (!bladWeryfikacji || user) return;
    setStatus('link_expired');
    setMessage(
      bladWeryfikacji.kod === 'otp_expired'
        ? t(
            'invitation.linkExpired',
            'Link aktywacyjny wygasł albo został już użyty. Poproś administratora o wysłanie nowego zaproszenia.'
          )
        : t(
            'invitation.verifyFailed',
            'Nie udało się zalogować z linku ({{code}}). Poproś administratora o nowe zaproszenie.',
            { code: bladWeryfikacji.kod }
          )
    );
  }, [bladWeryfikacji, user, t]);

  // Brak sesji bez błędu w URL-u (np. link otwarty w innej przeglądarce niż
  // ta z sesją): po limicie czasu pokazujemy wyjście, nie spinner.
  useEffect(() => {
    if (user || bladWeryfikacji) return;
    const timer = setTimeout(() => {
      setStatus((s) => (s === 'loading' ? 'no_session' : s));
      setMessage(
        (m) =>
          m ||
          t(
            'invitation.noSession',
            'Nie udało się ustalić Twojej sesji. Zaloguj się i otwórz link z zaproszenia ponownie — a jeśli link jest starszy niż godzina, poproś o nowy.'
          )
      );
    }, LIMIT_OCZEKIWANIA_NA_SESJE_MS);
    return () => clearTimeout(timer);
  }, [user, bladWeryfikacji, t]);

  useEffect(() => {
    const acceptInvitation = async () => {
      if (!user) {
        // Bez sesji nic tu nie zrobimy — o komunikatach decydują efekty wyżej
        // (błąd weryfikacji z URL-a albo limit oczekiwania na sesję).
        return;
      }

      if (!organizationId) {
        setStatus('error');
        setMessage(t('invitation.noOrganization', 'No organization specified in invitation'));
        return;
      }

      try {
        // Find the pending invitation for this user's email
        const { data: invitation, error: invError } = await supabase
          .from('organization_invitations')
          .select(
            `
            *,
            organization:organizations(id, name)
          `
          )
          .eq('email', user.email)
          .eq('organization_id', organizationId)
          .eq('status', 'pending')
          .gt('expires_at', new Date().toISOString())
          .single();

        if (invError || !invitation) {
          // Try with token if provided
          if (token) {
            const { data: tokenInvite, error: tokenError } = await supabase
              .from('organization_invitations')
              .select(
                `
                *,
                organization:organizations(id, name)
              `
              )
              .eq('token', token)
              .eq('status', 'pending')
              .gt('expires_at', new Date().toISOString())
              .single();

            if (tokenError || !tokenInvite) {
              setStatus('not_found');
              setMessage(
                t(
                  'invitation.notFound',
                  'Tego zaproszenia nie ma albo straciło ważność. Poproś administratora organizacji o nowe.'
                )
              );
              return;
            }

            // Token invitation found
            setOrganizationName((tokenInvite.organization as { name: string })?.name);
            await processInvitation(tokenInvite);
            return;
          }

          setStatus('not_found');
          setMessage(
            t(
              'invitation.notFound',
              'Tego zaproszenia nie ma albo straciło ważność. Poproś administratora organizacji o nowe.'
            )
          );
          return;
        }

        setOrganizationName((invitation.organization as { name: string })?.name);
        await processInvitation(invitation);
      } catch (err) {
        console.error('Error accepting invitation:', err);
        setStatus('error');
        setMessage(t('invitation.error', 'An error occurred while accepting the invitation'));
      }
    };

    const processInvitation = async (invitation: {
      id: string;
      token: string;
      organization_id: string;
      role: string;
    }) => {
      // Check if user is already a member
      const { data: existingMembership } = await supabase
        .from('user_organizations')
        .select('id')
        .eq('user_id', user!.id)
        .eq('organization_id', invitation.organization_id)
        .eq('status', 'active')
        .single();

      if (existingMembership) {
        setStatus('success');
        setMessage(t('invitation.alreadyMember', 'You are already a member of this organization'));
        return;
      }

      // Use the accept_invitation function. Funkcja filtruje po kolumnie
      // `token` (osobny UUID, nie `id`), więc wołanie z `invitation.id`
      // ZAWSZE rzucało „Invalid or expired invitation" i cały flow żył na
      // fallbacku — a fallback nie umiał domknąć statusu zaproszenia (RLS na
      // UPDATE wymaga owner/admin, pracownik zostawiał je „pending" i mógł się
      // sam dodać ponownie do wygaśnięcia). Ten RPC jest SECURITY DEFINER i sam
      // ustawia `accepted`.
      const { error: acceptError } = await supabase.rpc('accept_invitation', {
        invitation_token: invitation.token,
      });

      if (acceptError) {
        // Bez drogi zapasowej: bezpośredni INSERT do `user_organizations` jako zaproszony pada
        // zawsze (42P17, rekurencja polityk RLS — zmierzone w 793) i zasłaniał powód odmowy RPC
        // (limit kont, inny adres) — patrz `odmowaZaproszenia.ts`.
        console.error('Failed to accept invitation:', acceptError);
        const odmowa = rozpoznajOdmoweZaproszenia(acceptError);
        setStatus(odmowa.status);
        setMessage(t(odmowa.klucz, odmowa.zastepcze));
        return;
      }

      // Refresh user data to include new organization
      await refreshUserData();

      setStatus('success');
      setMessage(t('invitation.success', 'You have successfully joined the organization'));
    };

    acceptInvitation();
  }, [user, organizationId, token, t, refreshUserData]);

  const handleContinue = () => {
    navigate('/');
  };

  return (
    // Zaproszenie to ekran wejścia z maila — ten sam nagłówek co logowanie,
    // reset hasła i rejestracja (`UkladWejscia`). Wcześniej jako jedyny nosił
    // markę W KARCIE: sygnet 40 z „Insolitum Universe" wpisanym krojem
    // interfejsu obok (fałszywy napis) i nadtytułem w kolorze akcentu.
    // Nagłówkiem strony jest zadanie („Akceptacja zaproszenia"), nie marka —
    // nazwę marki niesie logo (`role="img"`), a czytnik przechodzący po
    // nagłówkach ma usłyszeć, po co tu przyszedł (WCAG 2.4.6). Wygląd
    // nadtytułu daje klasa, nie element.
    <UkladWejscia stalaGoraRem={POLOWA_NAJWYZSZEGO_STANU_REM}>
      <div className="card rounded-ids-2xl shadow-elev-4 p-8">
        <h1 className="ids-nadtytul text-center">{t('invitation.title', 'Team Invitation')}</h1>

        {/* Status Display */}
        <div className="mt-6 text-center">
          {status === 'loading' && (
            <>
              <div className="w-20 h-20 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Loader2 className="w-10 h-10 text-aurora-brand animate-spin" />
              </div>
              <h2 className="font-display text-xl font-semibold text-aurora-text mb-2">
                {t('invitation.processing', 'Processing Invitation')}
              </h2>
              <p className="text-aurora-muted">
                {t('invitation.pleaseWait', 'Please wait while we process your invitation...')}
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <UserCheck className={`w-10 h-10 ${IKONA_SUKCESU}`} />
              </div>
              <h2 className="font-display text-xl font-semibold text-aurora-text mb-2">
                {bezSierotek(t('invitation.welcomeTitle', 'Welcome to the Team!'))}
              </h2>
              {organizationName && (
                <div className="flex items-center justify-center gap-2 text-aurora-brand mb-4">
                  <Building2 className="w-5 h-5" />
                  <span className="font-medium">{organizationName}</span>
                </div>
              )}
              <p className="text-aurora-muted text-pretty mb-6">{bezSierotek(message)}</p>
              <button onClick={handleContinue} className="btn btn--primary btn--block">
                {t('invitation.continue', 'Continue to Dashboard')}
              </button>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <X className={`w-10 h-10 ${IKONA_BLEDU}`} />
              </div>
              <h2 className="font-display text-xl font-semibold text-aurora-text mb-2">
                {t('invitation.errorTitle', 'Invitation Error')}
              </h2>
              <p className="text-aurora-muted text-pretty mb-6">{bezSierotek(message)}</p>
              <button onClick={() => navigate('/')} className="btn btn--secondary btn--block">
                {t('invitation.goToDashboard', 'Go to Dashboard')}
              </button>
            </>
          )}

          {/* Brak zaproszenia to ostrzeżenie, nie sukces: do rundy 7b w kółku
              stał `Check`, czyli ten sam znaczek, co przy przyjęciu. */}
          {status === 'not_found' && (
            <>
              <div className="w-20 h-20 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <TriangleAlert className={`w-10 h-10 ${IKONA_OSTRZEZENIA}`} />
              </div>
              <h2 className="font-display text-xl font-semibold text-aurora-text mb-2">
                {t('invitation.notFoundTitle', 'Invitation Not Found')}
              </h2>
              <p className="text-aurora-muted text-pretty mb-6">{bezSierotek(message)}</p>
              <button onClick={() => navigate('/')} className="btn btn--secondary btn--block">
                {t('invitation.goToDashboard', 'Go to Dashboard')}
              </button>
            </>
          )}

          {(status === 'link_expired' || status === 'no_session') && (
            <>
              <div className="w-20 h-20 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className={`w-10 h-10 ${IKONA_OSTRZEZENIA}`} />
              </div>
              <h2 className="font-display text-xl font-semibold text-aurora-text mb-2">
                {status === 'link_expired'
                  ? t('invitation.linkExpiredTitle', 'Link aktywacyjny wygasł')
                  : t('invitation.noSessionTitle', 'Brak aktywnej sesji')}
              </h2>
              <p className="text-aurora-muted text-pretty mb-6">{bezSierotek(message)}</p>
              <button onClick={() => navigate('/login')} className="btn btn--primary btn--block">
                {t('invitation.goToLogin', 'Przejdź do logowania')}
              </button>
            </>
          )}
        </div>
      </div>
    </UkladWejscia>
  );
}
